import { formatPlainDate, type PlainDate } from "@sa-nu/shared-utils/date";
import clsx from "clsx";

import type { AccommodationFacilityType } from "../../../../__generated__/gql/graphql";
import { getAccommodationFacilitiesLabel } from "../../../utils/accommodation-facility";
import { buildRoomTitle } from "../../../utils/build-room-title";
import {
  STANDARD_CHECK_IN_TIME,
  STANDARD_CHECK_OUT_TIME,
} from "../../../utils/standard-check-in-out-time";
import type { SimpleTableRecord } from "../simple-table/simple-table";
import { SimpleTable } from "../simple-table/simple-table";

import {
  RoomChangeNotice,
  type RoomChangeNoticeMessages,
} from "./room-change-notice";

export type AccommodationAndPeriodMessages = {
  roomNumber: string;
  facility: string;
  options: string;
  checkIn: string;
  checkOut: string;
  nightsAndDays: (nights: number, days: number) => string;
  /** day は ISO 1-7 (1=月, 7=日) — Temporal.PlainDate.dayOfWeek と同一体系 */
  dayOfWeek: (day: number) => string;
  formatFacilityTypes: (types: AccommodationFacilityType[]) => string;
  /** isRoomTypeReservation=true のとき表示される RoomChangeNotice の文言。未指定時は日本語の既定値を使う。 */
  roomChangeNotice?: RoomChangeNoticeMessages;
};

const JA_MESSAGES: AccommodationAndPeriodMessages = {
  roomNumber: "部屋番号",
  facility: "施設",
  options: "オプション",
  checkIn: "チェックイン",
  checkOut: "チェックアウト",
  nightsAndDays: (nights: number, days: number) => `${nights}泊${days}日`,
  dayOfWeek: (day: number) =>
    ["", "月", "火", "水", "木", "金", "土", "日"][day] ?? "",
  formatFacilityTypes: (types) => getAccommodationFacilitiesLabel(types),
};

/**
 * 宿泊施設と宿泊期間を表示するコンポーネント
 */
export function AccommodationAndPeriod({
  accommodationName,
  accommodationCode,
  architectureName,
  checkInDate,
  checkOutDate,
  checkInTime = STANDARD_CHECK_IN_TIME,
  checkOutTime = STANDARD_CHECK_OUT_TIME,
  facilityTypes,
  ticketMasterName,
  className,
  messages = JA_MESSAGES,
  isRoomTypeReservation,
  roomTypeName,
  siteName,
}: {
  /** 施設名（例：山中湖1st 0308） */
  accommodationName: string;
  /** 宿泊施設コード（例：0308） */
  accommodationCode?: string;
  /** アーキテクチャ名（例：SANU CABIN BEE） */
  architectureName: string;
  /** チェックイン日（例：2024-10-29） */
  checkInDate: PlainDate;
  /** チェックアウト日（例：2024-11-02） */
  checkOutDate: PlainDate;
  /** チェックイン時刻（例：15:00） */
  checkInTime?: string;
  /** チェックアウト時刻（例：11:00） */
  checkOutTime?: string;
  /** 設備 */
  facilityTypes?: AccommodationFacilityType[];
  /** チケットマスター名 */
  ticketMasterName?: string;
  /** クラス名（スタイルを上書きする場合） */
  className?: string;
  messages?: AccommodationAndPeriodMessages;
  /**
   * 部屋タイプ予約として扱うか。
   * true のとき: 施設名直下に RoomChangeNotice を表示し、
   * 「オプション」行の値を facilityTypes ではなく roomTypeName にする。
   * siteName も渡された場合は見出しを2段構成にする。
   */
  isRoomTypeReservation?: boolean;
  /** 部屋タイプ名（例：SANU CABIN BEE TYPE-A）。isRoomTypeReservation=true のときに「オプション」行に表示する */
  roomTypeName?: string;
  /**
   * 拠点名（例：伊豆1st）。isRoomTypeReservation=true のときに2段見出しの大タイトルを
   * buildRoomTitle で生成するために使う。
   */
  siteName?: string;
}) {
  const data: SimpleTableRecord[] = [];
  if (accommodationCode) {
    data.push({ label: messages.roomNumber, value: accommodationCode });
  }
  data.push({ label: messages.facility, value: architectureName });
  if (isRoomTypeReservation && roomTypeName) {
    data.push({ label: messages.options, value: roomTypeName });
  } else if (facilityTypes && facilityTypes.length > 0) {
    data.push({
      label: messages.options,
      value: messages.formatFacilityTypes(facilityTypes),
    });
  }
  data.push({
    label: messages.checkIn,
    value: `${formatPlainDate(checkInDate, { withDayOfWeek: false })} (${messages.dayOfWeek(checkInDate.dayOfWeek)}) ${checkInTime}`,
  });
  data.push({
    label: messages.checkOut,
    value: `${formatPlainDate(checkOutDate, { withDayOfWeek: false })} (${messages.dayOfWeek(checkOutDate.dayOfWeek)}) ${checkOutTime}`,
  });
  const nights = checkInDate.until(checkOutDate).days;
  const days = nights + 1;
  data.push({
    label: "",
    value: messages.nightsAndDays(nights, days),
  });
  if (ticketMasterName) {
    data.push({
      label: "",
      value: ticketMasterName,
    });
  }

  return (
    <div
      className={clsx(
        "flex flex-col",
        isRoomTypeReservation ? "gap-y-[32px]" : "gap-y-[20px]",
        className,
      )}
    >
      {isRoomTypeReservation && siteName ? (
        <div className="flex flex-col gap-[4px]">
          <h2 className="font-dx text-dx-text-default text-[28px] leading-[40px] font-normal tracking-[-0.56px] [word-break:break-word]">
            {buildRoomTitle({
              siteName,
              architectureName,
            })}
          </h2>
          {roomTypeName && (
            <p className="font-notojp text-dx-text-default text-[16px] leading-[24px] font-bold [word-break:break-word]">
              {roomTypeName}
            </p>
          )}
        </div>
      ) : (
        <h2 className="text-[24px] wrap-break-word">{accommodationName}</h2>
      )}
      {isRoomTypeReservation && (
        <RoomChangeNotice messages={messages.roomChangeNotice} />
      )}
      <div className="w-full md:w-auto">
        <SimpleTable
          data={data}
          withoutSeparator
          fontSize={14}
          valueAlign="left"
          labelEnhanced
        />
      </div>
    </div>
  );
}
